import { useMemo, useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle, useMapEvents } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Navigation, Trash2, Circle as CircleIcon, Bookmark, MapPin as MapPinIcon } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

function makeIcon(color, featured = false) {
  const svg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="34" height="46" viewBox="0 0 34 46">
      <path d="M17 0C7.6 0 0 7.6 0 17c0 12 17 29 17 29s17-17 17-29C34 7.6 26.4 0 17 0z" fill="${color}" stroke="white" stroke-width="2"/>
      <circle cx="17" cy="17" r="7" fill="white"/>
      ${featured ? '<circle cx="27" cy="8" r="6" fill="#FBBF24" stroke="white" stroke-width="1.5"/>' : ""}
    </svg>`);
  return L.icon({
    iconUrl: `data:image/svg+xml;charset=UTF-8,${svg}`,
    iconSize: [34, 46], iconAnchor: [17, 46], popupAnchor: [0, -40],
  });
}

const roleColor = { farmer: "#16A34A", day_worker: "#2563EB", investor: "#B45309" };
const roleGradient = {
  farmer: "radial-gradient(circle at 30% 30%,#22C55E,#15803D)",
  day_worker: "radial-gradient(circle at 30% 30%,#3B82F6,#1D4ED8)",
  investor: "radial-gradient(circle at 30% 30%,#F59E0B,#B45309)",
};

function AutoFit({ items, userLoc }) {
  const map = useMap();
  useEffect(() => {
    const pts = items.filter((i) => i.lat && i.lng).map((i) => [i.lat, i.lng]);
    if (userLoc?.lat) pts.push([userLoc.lat, userLoc.lng]);
    if (pts.length === 0) return;
    if (pts.length === 1) { map.setView(pts[0], 12); return; }
    map.fitBounds(pts, { padding: [30, 30], maxZoom: 13 });
  }, [items, userLoc, map]);
  return null;
}

function ClickToDraw({ enabled, onSet }) {
  useMapEvents({
    click(e) {
      if (!enabled) return;
      onSet({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function CaptureCenter({ onCenter }) {
  useMapEvents({
    moveend(e) { const c = e.target.getCenter(); onCenter({ lat: c.lat, lng: c.lng }); },
  });
  return null;
}

function haversine(a, b) {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat), dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function makeClusterFn(gradient) {
  return function clusterIconCreate(cluster) {
    const count = cluster.getChildCount();
    const size = count < 10 ? 34 : count < 50 ? 42 : 52;
    return L.divIcon({
      html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${gradient};color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:${size / 2.8}px;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.2)">${count}</div>`,
      className: "gtf-cluster",
      iconSize: L.point(size, size, true),
    });
  };
}

const DRAW_KEY = "gtf_map_draw";

export default function ListingsMap({ items = [], role = "farmer", userLoc, height = 480 }) {
  const nav = useNavigate();
  const geoItems = useMemo(() => items.filter((i) => i.lat && i.lng), [items]);
  const color = roleColor[role] || "#16A34A";
  const gradient = roleGradient[role] || roleGradient.farmer;
  const userIcon = makeIcon("#EF4444");
  const initialCenter = geoItems[0] ? [geoItems[0].lat, geoItems[0].lng] : (userLoc?.lat ? [userLoc.lat, userLoc.lng] : [15.3, 76.9]);

  // Load persisted draw
  const persisted = (() => {
    try { const raw = localStorage.getItem(DRAW_KEY); return raw ? JSON.parse(raw) : null; } catch (e) { return null; }
  })();
  const [radius, setRadius] = useState(persisted?.radius || 20);
  const [drawMode, setDrawMode] = useState(false);
  const [center, setCenter] = useState(persisted?.center || null);
  const [mapCenter, setMapCenter] = useState(null);
  const [showSave, setShowSave] = useState(false);
  const [placeName, setPlaceName] = useState("");
  const [savingPlace, setSavingPlace] = useState(false);

  useEffect(() => {
    if (!center) { localStorage.removeItem(DRAW_KEY); return; }
    localStorage.setItem(DRAW_KEY, JSON.stringify({ center, radius }));
  }, [center, radius]);

  const filtered = useMemo(() => {
    if (!center) return geoItems;
    return geoItems.filter((i) => haversine(center, { lat: i.lat, lng: i.lng }) <= radius);
  }, [geoItems, center, radius]);

  const clusterIconCreate = useMemo(() => makeClusterFn(gradient), [gradient]);

  const openDirections = (lat, lng) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}${userLoc?.lat ? `&origin=${userLoc.lat},${userLoc.lng}` : ""}&travelmode=driving`;
    window.open(url, "_blank");
  };

  const saveHere = async () => {
    if (!placeName.trim()) return toast.error("Give this place a name");
    const pt = mapCenter || (userLoc?.lat ? userLoc : (geoItems[0] ? { lat: geoItems[0].lat, lng: geoItems[0].lng } : null));
    if (!pt) return toast.error("Pan the map to choose a location");
    setSavingPlace(true);
    try {
      await api.post("/places", { name: placeName.trim(), lat: pt.lat, lng: pt.lng });
      toast.success(`Saved: ${placeName.trim()}`);
      setPlaceName(""); setShowSave(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingPlace(false); }
  };

  return (
    <div className="rounded-3xl overflow-hidden border border-gray-100 premium-shadow relative" style={{ height }}>
      {/* Top-left: Draw radius */}
      <div className="absolute top-3 left-3 z-[1000] flex items-center gap-2 bg-white/95 backdrop-blur rounded-full px-3 py-2 border border-gray-200 shadow-lg">
        <button data-testid="draw-circle-toggle" onClick={() => { setDrawMode(!drawMode); if (drawMode && !center) setDrawMode(false); }}
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${drawMode ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700"}`}>
          <CircleIcon className="h-3.5 w-3.5" />{drawMode ? (center ? "Set radius below" : "Tap map to place") : (center ? `Radius ${radius}km` : "Draw radius")}
        </button>
        {center && (
          <>
            <input data-testid="radius-slider" type="range" min="1" max="200" value={radius} onChange={(e) => setRadius(parseInt(e.target.value))}
              className="w-32 accent-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700">{radius} km</span>
            <button data-testid="clear-circle" onClick={() => { setCenter(null); setDrawMode(false); }} className="h-6 w-6 rounded-full bg-red-50 text-red-600 flex items-center justify-center"><Trash2 className="h-3 w-3" /></button>
          </>
        )}
      </div>

      {/* Top-right: Save this place */}
      <div className="absolute top-3 right-3 z-[1000] flex flex-col items-end gap-2">
        <button data-testid="save-from-map" onClick={() => setShowSave(!showSave)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold bg-white/95 backdrop-blur border border-gray-200 shadow-lg text-emerald-700 hover:bg-emerald-50">
          <Bookmark className="h-3.5 w-3.5" />Save this place
        </button>
        {showSave && (
          <div className="rounded-2xl bg-white/95 backdrop-blur p-4 shadow-xl border border-gray-100 w-72">
            <div className="text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1"><MapPinIcon className="h-3 w-3" />Save the map center</div>
            <input data-testid="map-place-name" value={placeName} onChange={(e) => setPlaceName(e.target.value)}
              placeholder='e.g. "Village Market"' className="mt-2 w-full rounded-full border border-gray-200 px-4 py-2 text-sm" />
            <div className="mt-3 flex gap-2">
              <button onClick={() => setShowSave(false)} className="flex-1 py-2 rounded-full border border-gray-200 text-xs">Cancel</button>
              <button data-testid="map-save-btn" disabled={savingPlace} onClick={saveHere} className="flex-1 py-2 rounded-full bg-emerald-600 text-white text-xs font-semibold disabled:opacity-60">{savingPlace ? "Saving..." : "Save"}</button>
            </div>
            {mapCenter && <div className="mt-2 text-[10px] text-gray-500 text-center">📍 {mapCenter.lat.toFixed(4)}, {mapCenter.lng.toFixed(4)}</div>}
          </div>
        )}
      </div>

      <MapContainer center={initialCenter} zoom={6} scrollWheelZoom style={{ height: "100%", width: "100%", cursor: drawMode ? "crosshair" : "" }}>
        <TileLayer
          attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <AutoFit items={filtered} userLoc={userLoc} />
        <CaptureCenter onCenter={setMapCenter} />
        <ClickToDraw enabled={drawMode} onSet={setCenter} />
        {center && <Circle center={[center.lat, center.lng]} radius={radius * 1000} pathOptions={{ color, fillColor: color, fillOpacity: 0.12 }} />}
        {userLoc?.lat && (
          <Marker position={[userLoc.lat, userLoc.lng]} icon={userIcon}>
            <Popup>You are here</Popup>
          </Marker>
        )}
        <MarkerClusterGroup chunkedLoading iconCreateFunction={clusterIconCreate} showCoverageOnHover={false} spiderfyOnMaxZoom>
          {filtered.map((it) => (
            <Marker key={it.id} position={[it.lat, it.lng]} icon={makeIcon(color, it.is_featured)}>
              <Popup>
                <div className="min-w-[200px]">
                  <div className="font-semibold text-gray-900">{it.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{it.category}</div>
                  <div className="text-sm font-bold text-emerald-700 mt-1">₹{it.price}{it.unit ? ` /${it.unit}` : ""}</div>
                  <div className="text-[11px] text-gray-500 mt-1">
                    {[it.village, it.city, it.state].filter(Boolean).join(", ")}
                    {it.distance_km != null && <> · <b>{it.distance_km} km</b></>}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => nav(`/marketplace/${it.id}`)} className="flex-1 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">
                      View
                    </button>
                    <button onClick={() => openDirections(it.lat, it.lng)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs font-semibold">
                      <Navigation className="h-3 w-3" />Route
                    </button>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </div>
  );
}
